import ObjectId from "bson-objectid";

import api from "@/api";
import deepClone from "@/utils/deep-clone";

function generateDefaultTemplate(user) {
    // make sure this matches the backend version in sReviewCycles.generateDefaultTemplate

    return {
        _id: ObjectId().toString(),
        name: "",
        description: "",
        owners: [user],
        status: {
            globalStatus: "templateDraft",
            reminderFrequency: "every week",
            reminderDay: "Tuesday",
        },
        template: "classic",
        visibility: {
            reviewReviewee: {
                result: {
                    owner: "onceComplete",
                    reviewee: "yes",
                    manager: "onceManagerComplete",
                    additionalManager: "onceManagerComplete",
                },
                name: {
                    owner: "yes",
                    reviewee: "yes",
                    manager: "yes",
                    additionalManager: "yes",
                },
            },
            reviewManager: {
                result: {
                    owner: "onceComplete",
                    reviewee: "onceManagerShared",
                    manager: "yes",
                    additionalManager: "onceComplete",
                },
                name: {
                    owner: "yes",
                    reviewee: "yes",
                    manager: "yes",
                    additionalManager: "yes",
                },
            },
            // bear in mind that this will be overwrite
            // we will make sure this is the same as manager.
            reviewAdditionalManager: {
                result: {
                    owner: "onceComplete",
                    reviewee: "onceManagerShared",
                    manager: "yes", // actually this doesn't make sense that additionalManager can see manager's result.
                    additionalManager: "onceComplete",
                },
                name: {
                    owner: "yes",
                    reviewee: "yes",
                    manager: "yes",
                    additionalManager: "yes",
                },
            },
            reviewPeer: {
                result: {
                    owner: "onceComplete",
                    reviewee: "onceManagerShared",
                    manager: "onceManagerComplete",
                    additionalManager: "onceManagerComplete",
                },
                name: {
                    owner: "yes",
                    reviewee: "yes",
                    manager: "yes",
                    additionalManager: "yes",
                },
            },
            reviewDirectReport: {
                result: {
                    owner: "onceComplete", // other: "no"
                    reviewee: "onceManagerShared", // other: "onceComplete", "onceManagerSharedRevieweeComplete", "no"
                    manager: "onceManagerComplete", // other: "onceComplete", "onceManagerSharedRevieweeComplete", "no"
                    additionalManager: "onceManagerComplete", // other: "onceComplete", "onceManagerSharedRevieweeComplete", "no"
                },
                name: {
                    owner: "yes",
                    reviewee: "yes",
                    manager: "yes",
                    additionalManager: "yes",
                },
            },
        },
        participationRules: {
            include: {
                all: true,
                users: [],
                localTeams: [],
                locations: [],
            },
            exclude: {
                users: [],
                localTeams: [],
                locations: [],
                tenureInWeeksLessThan: 0,
            },
        },
        isTemplate: true,
        scopeLocked: false,
        questionsLocked: false,
        questionSettingsLocked: false,
        visibilityLocked: false,
        questions: { skills: [], settings: {} },
        customNotifications: {
            kickoff: null,
            daily: null,
            weekly: null,
        },
        managerOverwrittenBy: {},
    };
}

const state = {
    details: {},
    preview: {},
};

const getters = {
    details(state) {
        return id => deepClone(state.details[id] || {});
    },
    preview(state) {
        return state.preview;
    },
};

const actions = {
    create({ commit, rootGetters }, { payload: { template = {} } = {} }) {
        const user = rootGetters["users/current/user"];

        if (!user) {
            throw new Error();
        }

        // Generate new template based on defaults
        const newTemplate = {
            ...generateDefaultTemplate(user),
            ...template,
        };

        commit("updateDetails", { template: newTemplate });
    },

    get({ commit }, { payload: { id } }) {
        return api
            .get(`/api/reviewcycles/get/details/${id}`)
            .then(res => {
                const data = res.data;
                const template = data;

                const defaultTemplate = generateDefaultTemplate();

                if (!data.questions) {
                    template.questions = defaultTemplate.questions;
                }

                if (!data.participationRules) {
                    template.participationRules = defaultTemplate.participationRules;
                } else {
                    // match FE data structure

                    template.participationRules.include.locations = data.participationRules.include.locations.map(
                        function(l) {
                            return { name: l };
                        },
                    );
                    template.participationRules.exclude.locations = data.participationRules.exclude.locations.map(
                        function(l) {
                            return { name: l };
                        },
                    );
                    template.participationRules.include.users = data.participationRules.include.users.map(
                        function(u) {
                            return { name: u.fullname, _id: u._id };
                        },
                    );
                    template.participationRules.exclude.users = data.participationRules.exclude.users.map(
                        function(u) {
                            return { name: u.fullname, _id: u._id };
                        },
                    );
                }

                if (!data.visibility) {
                    template.visibility = defaultTemplate.visibility;
                }

                // editing templates
                template.questionsLocked = false;
                template.questionSettingsLocked = false;
                template.scopeLocked = false;
                template.visibilityLocked = false;

                if (!template.customNotifications) {
                    template.customNotifications = {
                        kickoff: null,
                        daily: null,
                        weekly: null,
                    };
                }

                commit("updateDetails", { template });
            })
            .catch(err => {
                if (err?.response?.status === 404) {
                    return;
                }

                throw err;
            });
    },

    update({ commit, state }, { payload: { template, scope } }) {
        const prevTemplate = getters.details(state)(template._id);

        // Basics
        if (scope === "basics") {
            return api
                .post("/api/reviewcycles/create", {
                    reviewCycle: template._id,
                    owners: template.owners,
                    name: template.name,
                    description: template.description,
                    isTemplate: true,
                    isNew: template.isNew,
                    templateForManagers: template.templateForManagers,
                    templateForEmployees: template.templateForEmployees,
                })
                .then(res => {
                    const data = res.data;

                    commit("updateDetails", {
                        template: {
                            ...prevTemplate,
                            scale: data.reviewCycle.scale,
                            isNew: false,
                        },
                    });
                });
        }

        // Scope
        if (scope === "scope") {
            return api.post("/api/reviewcycles/update/scope", template).then(res => {
                commit("updateDetails", {
                    template: {
                        ...prevTemplate,
                        ...res.data,
                    },
                });
            });
        }

        // Questions
        if (scope === "questions") {
            const questions = [];

            template.questions?.selected?.forEach?.(function(question) {
                if (question.name.length)
                    questions.push({
                        _id: question._id,
                        name: question.name,
                        description: question.description,
                        type: question.type,
                        roleReviewee: question.roleReviewee,
                        roleManager: question.roleManager,
                        rolePeer: question.rolePeer,
                        roleDirectReport: question.roleDirectReport,
                        private: question.private,
                        weight: question.weight,
                        scale: question.scale,
                        commentRequirement:
                            question.commentRequirement === "hidden" &&
                            question.ratingRequirement === "hidden" &&
                            question.answerType !== "multipleChoice"
                                ? "optional"
                                : question.commentRequirement,
                        ratingRequirement:
                            question.commentRequirement === "hidden" &&
                            question.ratingRequirement === "hidden" &&
                            question.answerType !== "multipleChoice"
                                ? "optional"
                                : question.ratingRequirement,
                        answerType: question.answerType,
                        multipleChoiceOptions: question.multipleChoiceOptions,
                        allowMultipleAnswers: question.allowMultipleAnswers,
                        translations: question.translations,
                    });
            });

            return api
                .post("/api/reviewcycles/update/questionsettings", {
                    reviewCycle: template._id,
                    questions: questions,
                    companySkills: template.questions?.companySkills,
                    teamSkills: template.questions?.teamSkills,
                    goals: template.questions?.goals,
                    customScoresEnabled: template.questions?.customScoresEnabled,
                    scale: template.scale,
                })
                .then(function(response) {
                    template.questions.selected.forEach(function(question, idx) {
                        if (!question._id) {
                            var q = response.data.questions.skills.find(function(q) {
                                return q.name == question.name && !q._id;
                            });
                            template.questions.selected[idx]._id = q._id;
                        }
                    });

                    commit("updateDetails", { template });
                });
        }

        // Goals
        if (scope === "goals") {
            return api.post("/api/reviewcycles/update/goals", { reviewCycleId: template._id });
        }

        // Skill Assignment
        if (scope === "skillAssignment") {
            return api.post("/api/reviewcycles/update/skillAssignment", {
                reviewCycleId: template._id,
            });
        }

        // Visibility
        if (scope === "visibility") {
            return api.post("/api/reviewcycles/update/visibility", template).then(() => {
                commit("updateDetails", { template });
            });
        }

        // Timeline
        if (scope === "timeline") {
            const data = {
                reviewCycle: template._id,
                isTemplate: true,
                selfEvaluationsEnabled: template.selfEvaluationsEnabled,
                peerReviewsEnabled: template.peerReviewsEnabled,
                peerNominationsEnabled: template.peerNominationsEnabled,
                peerApprovalEnabled: template.peerApprovalEnabled,
                peerAutoApprovalEnabled: template.peerAutoApprovalEnabled,
                managerReviewsEnabled: template.managerReviewsEnabled,
                conversationsEnabled: template.conversationsEnabled,
                globalStatus: template.status.globalStatus,
                peerNominationsDelay: template.status.peerNominationsDelay,
                peerApprovalDelay: template.status.peerApprovalDelay,
                peerReviewsDelay: template.status.peerReviewsDelay,
                managerReviewsDelay: template.status.managerReviewsDelay,
                conversationsDelay: template.status.conversationsDelay,
            };

            // call API

            return api.post("/api/reviewcycles/update/timeline", data).then(() => {
                commit("updateDetails", { template });
            });
        }

        // Notifications
        if (scope === "notifications") {
            return api.post("/api/reviewcycles/update/notifications", template).then(res => {
                const data = res.data;

                commit("updateDetails", {
                    template: {
                        ...prevTemplate,
                        status: {
                            ...prevTemplate.status,
                            nextReminderOn: data.status.nextReminderOn,
                        },
                    },
                });
            });
        }
    },

    updateStatus({ commit, state }, { payload: { id, status } }) {
        const prevTemplate = getters.details(state)(id);

        return api
            .post("/api/reviewcycles/update/status", {
                reviewCycle: id,
                newStatus: status,
            })
            .then(res => {
                const data = res.data;

                commit("updateDetails", {
                    template: {
                        ...prevTemplate,
                        status: data.status,
                    },
                });
            });
    },

    preview({ commit }, { payload: { id, role, userId } }) {
        return api
            .get("/api/reviewcycles/questions/preview/" + id + "/" + role + "/" + userId)
            .then(res => {
                const data = { ...res.data, role };
                commit("updatePreview", { preview: data });
            });
    },

    deactivate(_, { payload: { id } }) {
        return api.post("/api/reviewcycles/update/status", {
            reviewCycle: id,
            newStatus: "templateDeactivated",
        });
    },
};

const mutations = {
    updateDetails(state, { template }) {
        if (!template._id) {
            return;
        }

        state.details[template._id] = template;
    },

    updatePreview(state, { preview }) {
        state.preview = preview;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
